import clsx from 'clsx';
import type * as React from 'react';

import {
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    BLOCK_PARAGRAPH,
} from '@gitbook/doc-core';
import type { Block, Document, Fragment } from '@gitbook/slate';
import type { BlockProps } from '@gitbook/slate-react';
/**
 * A basic block wrapper that wraps the block node with appropriate spacing.
 */
export function BasicBlockWrapper(
    props: BlockProps & {
        tag?: React.ElementType;
        style?: React.CSSProperties;
        className?: string;
    }
): React.ReactElement {
    const { node, parent, children, attributes, className, tag: Tag = 'div', style } = props;

    const blockSpacing = getBasicBlockSpacing(node, parent);

    return (
        <Tag
            {...attributes}
            data-block-content={node.key}
            className={clsx(blockSpacing, className)}
            style={style}
        >
            {children}
        </Tag>
    );
}

/**
 * Helper method to determine the spacing between block nodes.
 */
function getBasicBlockSpacing(node: Block, parent: Block | Document | Fragment): string {
    const index = parent.getChildIndex(node.key);
    const isFirst = index === 0;

    let blockSpacing = '';
    let parentOverride = '';

    switch (node.type) {
        case BLOCK_HEADING_1:
        case BLOCK_HEADING_2:
        case BLOCK_HEADING_3:
            blockSpacing = 'mt-6 mb-2';
            break;
        case BLOCK_PARAGRAPH:
            blockSpacing = 'my-2';
            break;
        case BLOCK_LIST_OL:
        case BLOCK_LIST_UL:
        case BLOCK_LIST_TASKS:
            blockSpacing = 'mt-4 mb-2';
            break;
        case BLOCK_LIST_ITEM:
            blockSpacing = 'mt-2';
            break;
        default:
            blockSpacing = 'my-4';
    }

    switch (parent.type) {
        case BLOCK_LIST_ITEM: {
            parentOverride = 'mt-2 mb-0';
            break;
        }
    }

    if (isFirst) {
        parentOverride = 'mt-0';
    }

    return parentOverride || blockSpacing;
}
